import type { Company } from '../@types/Company';
import type { Owner } from '../@types/Owner';

export interface ServedCompany {
  id: number;
  name: string;
  siren: string;
  coproIds: number[];
  details: { address?: string; postalCode?: string; city?: string };
  brandingSettings: {
    commercialName: string | null;
    brandingImageName: string | null;
    color1: string | null;
    referents: Array<{ id: string | null; firstname: string | null; lastname: string | null; email: string | null }>;
  };
}

export const resolveOwnerCoproId = (owner: Owner): number | null =>
  owner.dpe?.copro?.id ?? owner.dpe?.coproId ?? owner.incomingCallCopro?.id ?? null;

/**
 * Finds the company served by the current subcontractor that holds this owner's copro,
 * or null when the owner belongs to the current company.
 */
export const findServedCompany = (owner: Owner, servedCompanies: ServedCompany[]): ServedCompany | null => {
  const coproId = resolveOwnerCoproId(owner);
  if (!coproId) {
    return null;
  }
  return servedCompanies.find((served) => served.coproIds.includes(coproId)) ?? null;
};

/**
 * Mails and letters to an owner carry the identity of the syndic of their copro: a
 * subcontractor's collaborator writes on behalf of that company, not as the subcontractor.
 */
export const resolveOwnerCompany = (
  owner: Owner,
  company: Company | null,
  servedCompanies: ServedCompany[],
): Company | null => {
  const served = findServedCompany(owner, servedCompanies);
  if (!served || !company) {
    return company;
  }
  return {
    ...company,
    id: served.id,
    name: served.name,
    siren: served.siren,
    details: served.details as Company['details'],
    brandingSettings: {
      ...company.brandingSettings,
      commercialName: served.brandingSettings.commercialName ?? undefined,
      brandingImageName: served.brandingSettings.brandingImageName ?? undefined,
      color1: served.brandingSettings.color1 ?? undefined,
      referents: served.brandingSettings.referents as NonNullable<Company['brandingSettings']>['referents'],
    },
  };
};
